/**
 * PD-14845 — "primary" licence resolution.
 *
 * A client can hold several licences of the same type at one address
 * (PD-14844). Exactly one of them is the "primary" — the licence the ERP
 * integration and order validation act on. This module resolves which one,
 * from a group already scoped to a single (address, type).
 *
 * Precedence, highest first:
 *   1. an explicitly-flagged primary (`isPrimary`)      — the user's choice
 *   2. the only licence in the group                    — implicitly primary
 *   3. the sole ERP-sourced row (non-null `externalId`) — the ERP licence
 *   4. otherwise `null`                                 — ambiguous; a user must pick
 */

export interface PrimaryLicenseCandidate {
	externalId: string | null;
	isPrimary?: boolean | null;
}

/**
 * Resolve the primary licence within a group scoped to ONE address and ONE
 * type. Returns the winning row, or `null` when the choice is ambiguous (2+
 * licences with no flag and no single ERP row). Callers MUST pre-scope the
 * input; this function does not group by address or type.
 */
export function resolvePrimaryLicense<T extends PrimaryLicenseCandidate>(
	group: ReadonlyArray<T>,
): T | null {
	// 1. An explicit user flag always wins. At most one primary per group is
	//    DB-enforced; if two are somehow flagged, treat as ambiguous rather
	//    than picking arbitrarily.
	const flagged = group.filter((license) => license.isPrimary === true);
	if (flagged.length === 1) {
		return flagged[0] ?? null;
	}
	if (flagged.length > 1) {
		return null;
	}
	// 2. A lone licence of the type is primary implicitly (no flag needed).
	if (group.length === 1) {
		return group[0] ?? null;
	}
	// 3. The sole ERP-sourced row (the upstream integration writes exactly one
	//    externalId-bearing row per type — verified in the data).
	const erpRows = group.filter(
		(license) => license.externalId != null && license.externalId !== '',
	);
	if (erpRows.length === 1) {
		return erpRows[0] ?? null;
	}
	// 4. Ambiguous — a primary must be designated explicitly.
	return null;
}

/**
 * Whether the "primary" concept is meaningful for this (address, type) group:
 * true once there are two or more licences, so the UI can offer a "Make
 * primary" control. A lone licence is always implicitly primary and needs no
 * affordance.
 */
export function isPrimaryEligible<T extends PrimaryLicenseCandidate>(
	group: ReadonlyArray<T>,
): boolean {
	return group.length >= 2;
}

/**
 * Whether a primary MUST be chosen explicitly for this group — 2+ licences and
 * no primary can be derived automatically (no flag, and not a lone ERP row).
 * This is the "needs attention" case the ERP integration declines to act on.
 */
export function requiresPrimarySelection<T extends PrimaryLicenseCandidate>(
	group: ReadonlyArray<T>,
): boolean {
	return group.length >= 2 && resolvePrimaryLicense(group) == null;
}
